import { useEffect, useMemo, useRef } from "react";
import type { PointerEvent as ReactPointerEvent, ReactElement } from "react";
import type { TicketKalender } from "@kundeninfo/api-client";
import { hausregelTooltip, streifenMuster, useDiagramm } from "@kundeninfo/ui/diagramm";
import type { DiagrammThema } from "@kundeninfo/ui/diagramm";
import {
  AXIS_PX, axisLabel, dayMs, EDGE_MIN_PX, gridLines, LANE_PX, nearestStage, ROW_PAD_PX, shading, TEXT_MIN_PX, windowFromMs,
} from "./timelineModel.js";
import type { BarState, layout, TimelineBar, TimelineGroup, TimelineMarker, TimelineStage, TimelineWindow } from "./timelineModel.js";
import { popupFuerTag } from "./ticketFormat.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import { TIMELINE_TEXT } from "./timelineTexts.js";
import "./TicketTimeline.css";

/**
 * ZEICHENFLÄCHEN DER ZEITACHSE (Spec Plan-Ansicht S9–S12, S14, S14a, S15; Plan P1–P5) — eine Optionsfabrik für die
 * klebende Kopfachse (`axisOption`) und die Gruppenflächen (`bodyOption`), dazu die Komponente, die beide über den
 * Baustein `@kundeninfo/ui/diagramm` zeichnet. ECharts selbst wird hier nie importiert; Farben kommen aus dem Thema
 * (Tokens), Texte aus `timelineTexts.ts`. Das Fenster ist Zustand der Ansicht (P3): Jede Fläche bekommt es als
 * `dataZoom.startValue/endValue` einer um je eine Spanne breiteren Achse (`basis`), Gesten melden ein neues Fenster über
 * `onWindow`, nie per Mausrad (Hausregel 14.10.2026).
 *
 * Terminebene (Spec D24, T19): eine dritte `custom`-Serie `markers` neben `bars` und `toggles` zeichnet die Marken der
 * Gruppen „Meilensteine“ und je Quelle — Raute bzw. Punkt mittig am Tag, Überzug bis „jetzt“, in der Stufe Tag die
 * Nummer daneben. Teamgruppen tragen die Serie leer, Termingruppen `bars` und `toggles` leer; `barShapes` bleibt (P1).
 */

const DAY_MS = 86_400_000;
/** Kopfachse (AXIS_PX hoch): Beschriftung, Tönung, Raster und „jetzt“-Linie in den oberen 28 px — dort trägt jeder Tag
 *  sein Popup (S15) —, darunter die Beschriftung „jetzt“ (Spec S14a, Skizze). */
const AXIS_GRID = { top: 0, bottom: 16 } as const;
/** Raster und „jetzt“ der Kopfachse nur im unteren Teil (Anteil der y-Achse 0…1): die Beschriftung darüber bleibt frei. */
const AXIS_STRIP = 0.38;
export const LONG_PRESS_MS = 450, LONG_PRESS_SLOP_PX = 8;
const TOGGLE_PX = 16, TOGGLE_PX_PHONE = 24;
/** Trefferfläche des Aufklappers am Telefon: `--treffer` (44 px, S13), soweit der sichtbare Balken sie hergibt (W1). */
const TOGGLE_HIT_PX_PHONE = 44;
const DONE_OPACITY = 0.55, HATCH_OPACITY = 0.6, SHADE_OPACITY = 0.5;
/** A22, 571: der Rest hinter dem Füllgrad mit Deckkraft × 0,4; Schriftrand in der vollen Zustandsfarbe, damit Schrift in
 *  `--flaeche` auf dem blassen Rest denselben Kontrast hat wie auf der Füllung (zrender zeichnet den Rand unter die
 *  Schrift); die Überschreitung mindestens 2 px breit, damit auch eine knappe sichtbar bleibt (Review G3). */
const EFFORT_REST_OPACITY = 0.4, EFFORT_TEXT_STROKE_PX = 3, EFFORT_OVERRUN_MIN_PX = 2;
/** Aufklapper am Eltern-Balken (S11, S13) — Zeichen, keine Sprache. */
const GLYPH = { expanded: "▾", collapsed: "▸" } as const;

/** Auswahl der Zeilenspalte (Aufgabe 4, `TimelineSelection` in `TicketTimelineRows.tsx`): Zeile und Balken in Rangfolge. */
type Selection = { rowId: string; barIndex: number | null };
type TimelineLayout = ReturnType<typeof layout>;

export type TimelineAxisOptions = { window: TimelineWindow; stage: TimelineStage | null; heute: string; nowMs: number;
  kalender: readonly TicketKalender[]; thema: DiagrammThema; phone: boolean };

export type TimelineBodyOptions = TimelineAxisOptions & { group: TimelineGroup; layout: TimelineLayout;
  block: { from: number; to: number; top: number; height: number }; selection: Selection | null; heute: string;
  /** Hausfarbe je Unternehmen (`board.kalender[].colorHex`): nur als 3-px-Kante am Balken (S10, 537). */
  companyColors: ReadonlyMap<string, string>;
  /** Name je Team-Kennung: Popup-Zeile „auch unter ‹Hauptteam›“ an schraffierten Wiederholungen (533, P15). */
  teamNames: ReadonlyMap<string, string> };

type ChartEvents = { onWindow: (w: TimelineWindow) => void };
export type TicketTimelineChartProps = ({ kind: "axis" } & TimelineAxisOptions & ChartEvents)
  | ({ kind: "body" } & TimelineBodyOptions & ChartEvents & { onBarClick: (id: string) => void; onToggle: (id: string) => void;
      /** Klick auf eine Marke (D24): ihr Schlüssel (`item.key`). */
      onMarkerClick: (key: string) => void;
      onHover?: (id: string | null) => void;
      /** „Zeitraum aufziehen“ (S14 Zoom): Rahmen ziehen statt blättern, über das versteckte Werkzeug dataZoom. */
      frameMode?: boolean;
      /** K1: zeigt die Auswahl ihr Popup? `false` (Dialog offen) lässt den Auswahlrahmen stehen und schließt nur das Popup. */
      selectionTip?: boolean });

// ── Optionsfabrik ──────────────────────────────────────────────────────────────────────────────────────────────

/** Ein Balken der Fläche mit Datenpunkt P1 `[fromMs, toExclusiveMs, laneTopPx, laneBottomPx]` (px ab Blockbeginn). */
type BarItem = { bar: TimelineBar; rowId: string; value: [number, number, number, number]; selected: boolean };
type RenderApi = { value: (dim: number) => number; coord: (point: [number, number]) => number[] };
type RenderParams = { dataIndex: number; coordSys: { x: number; width: number } };
type Shape = Record<string, unknown>;
type Hatch = ReturnType<typeof streifenMuster> & { rotation: number; scaleX: number; scaleY: number };
type ShapeContext = { thema: DiagrammThema; phone: boolean; nowMs: number; companyColors: ReadonlyMap<string, string>;
  hatch: Readonly<Record<"flaeche" | "text", Hatch>> };

/** Schraffur 533 (M2, Spec Z. 240): Muster des Bausteins in der Schriftfarbe des Zustands (kontraststark zur Fläche, A6),
 *  um 90° gedreht und auf 6 px verdichtet — Gegenrichtung und andere Dichte als „wartend“ (zrender dreht Muster per
 *  `rotation`/`scaleX`/`scaleY`). */
const hatchOf = (ink: string): Hatch => ({ ...streifenMuster(ink, "transparent"), rotation: Math.PI / 2, scaleX: 0.75, scaleY: 0.75 });

const isoDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** Fläche und Schrift je Zustand (S10): Fläche aus dem Zustandstoken (wartend gestreift, erledigt blass), Schrift auf
 *  den vollen Zustandsflächen in `--flaeche` — im hellen Thema weiß, im dunklen dunkel auf den dort hellen Tönen. */
const LOOK: Readonly<Record<BarState, { area: "fehler" | "warnung" | "ok" | "aus"; ink: "flaeche" | "text" }>> = {
  OVERDUE: { area: "fehler", ink: "flaeche" }, DUE_TODAY: { area: "warnung", ink: "flaeche" }, OPEN: { area: "ok", ink: "flaeche" },
  WAITING: { area: "aus", ink: "text" }, DONE: { area: "aus", ink: "text" },
};

/** Popup im Hausregel-Stil (P4) als DOM-Knoten, Text nur über `textContent` — Tickettitel werden nie HTML. Nicht
 *  richText: das zeichnet auf die Fläche selbst und würde an einer 28 px hohen Gruppe abgeschnitten (Sichtprobe); der
 *  Knoten hängt am `body`, Schrift 11 px und höchstens 260 px aus `hausregelTooltip()`, Zeilen über `\n` (`.tl__popup`). */
const popup = (): Record<string, unknown> => {
  const regel = hausregelTooltip();
  return { ...regel, renderMode: "html", appendTo: "body", extraCssText: `${String(regel.extraCssText ?? "")}z-index:var(--z-popup);` };
};
function popupNode(text: string): HTMLElement | string {
  if (text === "") return "";
  const node = document.createElement("div");
  node.className = "tl__popup";
  node.textContent = text;
  return node;
}

const LINES = { silent: true, symbol: ["none", "none"], label: { show: false }, lineStyle: { type: "solid" }, emphasis: { disabled: true } };

/** Gemeinsame Grundlage von Kopfachse und Gruppenfläche (P3, P5). Das Fenster steht als `dataZoom.startValue/endValue`;
 *  die Achse reicht eine Spanne weiter nach links und rechts (`reach`), denn ECharts verschiebt und verkleinert einen
 *  dataZoom nur innerhalb der Achse — mit Achse = Fenster ginge weder Ziehen noch Herauszoomen (Sichtprobe). Raster,
 *  Tönung und Beschriftung decken die ganze Achse, damit eine laufende Geste nichts Leeres aufdeckt. */
function basis(o: TimelineAxisOptions, labelled: boolean) {
  const t = o.thema;
  const start = dayMs(o.window.from), end = dayMs(o.window.to) + DAY_MS, span = end - start;
  const reach: TimelineWindow = { from: isoDay(start - span), to: isoDay(end + span - DAY_MS) };
  const stage = o.stage ?? nearestStage(o.window);
  return {
    stage,
    days: Array.from({ length: (3 * span) / DAY_MS }, (_, i) => start - span + i * DAY_MS),
    xAxis: { type: "time", min: start - span, max: end + span },
    dataZoom: [{ startValue: start, endValue: end }],
    shade: { silent: !labelled, label: { show: false }, emphasis: { disabled: true },
      itemStyle: { color: t.raster, opacity: SHADE_OPACITY },
      data: shading(reach, o.kalender).map(({ day }) =>
        [{ xAxis: dayMs(day), name: popupFuerTag(day, o.kalender) }, { xAxis: dayMs(day) + DAY_MS }]) },
    raster: gridLines(reach, stage).map(({ day, strong }) =>
      ({ xAxis: dayMs(day), lineStyle: { color: t.raster, width: strong ? 2 : 1 } })),
    now: { xAxis: o.nowMs, lineStyle: { color: t.jetzt, width: 1 },
      label: labelled ? { show: true, formatter: TIMELINE_TEXT.now, position: "start", distance: 2, color: t.jetzt, fontSize: 11 }
        : { show: false } },
  };
}

/** Kopfachse (S14a): Zeit oben mit der Beschriftung der Stufe, Tönung mit Popup „Wochenende“ bzw. „Feiertag: …“
 *  (`popupFuerTag`), „jetzt“-Linie MIT Beschriftung. Eine leere `custom`-Serie trägt Flächen und Linien. */
export function axisOption(o: TimelineAxisOptions): Record<string, unknown> {
  const t = o.thema;
  const b = basis(o, true);
  return {
    grid: { left: 0, right: 0, ...AXIS_GRID, containLabel: false, outerBoundsMode: "none" },
    xAxis: { ...b.xAxis, position: "top", splitLine: { show: false }, axisLine: { lineStyle: { color: t.raster } },
      axisTick: { customValues: b.raster.map((r) => r.xAxis), inside: true, length: 4, lineStyle: { color: t.raster } },
      axisLabel: { customValues: b.days.filter((ms) => axisLabel(isoDay(ms), b.stage) !== ""),
        formatter: (ms: number) => axisLabel(isoDay(ms), b.stage), color: t.leise, fontSize: 11, align: "left", inside: true, margin: 6,
        hideOverlap: true } },
    yAxis: { type: "value", min: 0, max: 1, show: false },
    dataZoom: b.dataZoom,
    tooltip: { ...popup(), formatter: (p: { componentType?: string; name?: string }) =>
      popupNode(p.componentType === "markArea" ? p.name ?? "" : "") },
    series: [{ type: "custom", name: "axis", data: [], renderItem: () => null, markArea: { ...b.shade, z: -1 },
      markLine: { ...LINES, data: [b.now, ...b.raster].map((m) => [{ ...m, yAxis: 0 }, { xAxis: m.xAxis, yAxis: AXIS_STRIP }]) } }],
  };
}

/** Die Balken eines Blocks in Zeilenfolge, je Zeile die sichtbaren in Rangfolge (`row.bars`, A10); `laneTop` liegt
 *  genau auf `layout` (Nachtrag: ohne ROW_PAD_PX/2 — die Zeichnung zentriert die Spuren in der Zeile). */
function blockItems(o: TimelineBodyOptions): BarItem[] {
  return o.group.rows.slice(o.block.from, o.block.to).flatMap((row, i) => {
    const top = o.layout.rows[o.block.from + i]!.top - o.block.top;
    return row.bars.map((bar, k): BarItem => ({ bar, rowId: row.id,
      value: [dayMs(bar.from), dayMs(bar.to) + DAY_MS, top + bar.lane * LANE_PX, top + (bar.lane + 1) * LANE_PX],
      selected: o.selection !== null && o.selection.rowId === row.id && o.selection.barIndex === k }));
  });
}

/** Lage eines Datenpunkts in Pixeln: Balken um ROW_PAD_PX/2 versetzt und 1 px Luft je Spur, Mindestbreite 4 px
 *  (Telefon 3 px); `left`/`right` = sichtbarer Teil (über den Fensterrand reichende Balken schreiben bis zum Rand). */
function laneBox(api: RenderApi, grid: RenderParams["coordSys"], phone: boolean) {
  const at = (ms: number): number => api.coord([ms, 0])[0]!;
  const [x0 = 0, y0 = 0] = api.coord([api.value(0), api.value(2)]);
  const [x1 = 0, y1 = 0] = api.coord([api.value(1), api.value(3)]);
  const w = Math.max(x1 - x0, phone ? 3 : 4);
  return { at, x0, w, top: y0 + ROW_PAD_PX / 2 + 1, h: y1 - y0 - 2, left: Math.max(x0, grid.x),
           right: Math.min(x0 + w, grid.x + grid.width), minW: phone ? 3 : 4 };
}

/** S9–S11, 533, 538: Vorlauf, Zustandsfläche, Überzug bis „jetzt“, offenes Ende (voll bis „jetzt“, dann Verlauf über
 *  einen Rasterschritt), Schraffur der Wiederholung, Kante in der Hausfarbe ab 24 px, Text und „2/3“ ab 60 px sichtbarer
 *  Breite, Rahmen der Auswahl. Mit Aufwand (A22, 571) wird die Zustandsfläche zum blassen Grund, darauf von links die
 *  Füllung (Füllgrad × gezeichnete Breite) und rechts die Überschreitung; die Schrift bekommt einen Rand. */
function barShapes(api: RenderApi, { bar, selected }: BarItem, grid: RenderParams["coordSys"], c: ShapeContext): Shape {
  const t = c.thema;
  const box = laneBox(api, grid, c.phone);
  const { at, x0, top, h, left } = box;
  const w = bar.openEnd ? Math.max(at(c.nowMs) - x0, box.minW) : box.w;
  const right = Math.min(x0 + w, box.right);
  const area = t[LOOK[bar.state].area];
  const ink = t[LOOK[bar.state].ink];
  const rect = (x: number, width: number, fill: unknown, extra: Shape = {}): Shape =>
    ({ type: "rect", shape: { x, y: top, width, height: h, r: 2 }, style: { fill, ...extra } });
  const children: Shape[] = [];
  if (bar.leadFrom !== null) {
    const xLead = at(dayMs(bar.leadFrom));
    children.push({ type: "rect", shape: { x: xLead, y: top + h / 2 - 1, width: x0 - xLead, height: 2 }, style: { fill: t.linieStark } });
  }
  const surface = bar.state === "WAITING" ? streifenMuster(area, t.flaeche) : area;
  const opacity = bar.state === "DONE" ? DONE_OPACITY : 1;
  const look: Shape = opacity === 1 ? {} : { opacity };
  const effort = bar.effort;
  const rest: Shape = { opacity: opacity * EFFORT_REST_OPACITY };
  // 571: auf einem überfälligen Balken ist die Fläche schon `fehler` — dort die Warnfarbe, damit das Ende sichtbar bleibt.
  const overColor = bar.state === "OVERDUE" ? t.warnung : t.fehler;
  children.push(rect(x0, w, surface, effort === null ? look : rest));
  if (effort !== null && effort.fill > 0) children.push(rect(x0, w * effort.fill, surface, look));
  if (effort !== null && effort.overrun > 0) {
    const over = Math.max(w * effort.overrun, EFFORT_OVERRUN_MIN_PX);
    children.push(rect(x0 + w - over, over, overColor));
  }
  if (bar.overdueFrom !== null) {
    const xDue = at(dayMs(bar.overdueFrom) + DAY_MS), xNow = at(c.nowMs);
    if (xNow > xDue) children.push(rect(xDue, xNow - xDue, streifenMuster(t.fehler, t.flaeche)));
  }
  if (bar.openEnd) {
    // Review G1: der Verlauf setzt das rechte Ende fort — blass bei Füllgrad < 1, in der Farbe der Überschreitung.
    const endColor = effort !== null && effort.overrun > 0 ? overColor : area;
    children.push(rect(x0 + w, at(DAY_MS) - at(0),
      { type: "linear", x: 0, y: 0, x2: 1, y2: 0, colorStops: [{ offset: 0, color: endColor }, { offset: 1, color: "transparent" }] },
      effort !== null && effort.fill < 1 ? rest : {}));
  }
  if (bar.isDuplicate) children.push(rect(x0, w, c.hatch[LOOK[bar.state].ink], { opacity: HATCH_OPACITY }));
  if (w >= EDGE_MIN_PX) {
    children.push({ type: "rect", shape: { x: x0, y: top, width: 3, height: h }, style: { fill: c.companyColors.get(bar.card.companyId) ?? t.text } });
  }
  const visible = right - left;
  if (visible >= TEXT_MIN_PX) {
    const mark = bar.children === 0 ? null
      : bar.canExpand ? HIERARCHY_TEXT.progress(bar.card.subtaskDone, bar.children) : TIMELINE_TEXT.childrenMark(bar.children);
    const markPx = mark === null || visible < TEXT_MIN_PX + mark.length * 7 ? 0 : mark.length * 7 + 6;
    const x = left + 8 + (bar.canExpand ? (c.phone ? TOGGLE_PX_PHONE : TOGGLE_PX) : 0);
    // Review M1: nur Schrift in `--flaeche` verliert auf dem blassen Rest; Schrift in `--text` (wartend, erledigt) gewinnt dort.
    const halo: Shape = effort === null || LOOK[bar.state].ink !== "flaeche" ? {} : { stroke: area, lineWidth: EFFORT_TEXT_STROKE_PX };
    children.push({ type: "text", style: { x, y: top + h / 2, text: `${bar.card.displayNumber} ${bar.card.title}`, fill: ink,
      fontSize: 12, verticalAlign: "middle", overflow: "truncate", ellipsis: "…", width: Math.max(right - x - 8 - markPx, 0), ...halo } });
    if (markPx > 0) {
      children.push({ type: "text", style: { x: right - 6, y: top + h / 2, text: mark, fill: ink, fontSize: 11, align: "right",
        verticalAlign: "middle", ...halo } });
    }
  }
  if (selected) {
    children.push({ type: "rect", shape: { x: x0 - 1, y: top - 1, width: w + 2, height: h + 2, r: 3 },
      style: { fill: "none", stroke: t.text, lineWidth: 2 } });
  }
  return { type: "group", children };
}

/** Aufklapper ▸/▾ als eigener Datenpunkt (P1): Trefferfläche 16 px (Telefon 24 px) am sichtbaren Balkenbeginn — nur,
 *  wenn der Balken dafür breit genug ist (sonst Leertaste in der Zeilenspalte oder der Dialog). */
function toggleShapes(api: RenderApi, bar: TimelineBar, grid: RenderParams["coordSys"], c: ShapeContext): Shape {
  const { top, h, left, right } = laneBox(api, grid, c.phone);
  const size = c.phone ? TOGGLE_PX_PHONE : TOGGLE_PX;
  if (right - left < size + 8) return { type: "group", children: [] };
  // W1: am Telefon so breit wie `--treffer` (höchstens bis 4 px vor dem sichtbaren Ende), senkrecht die ganze Spur.
  const hit = c.phone ? Math.max(size, Math.min(TOGGLE_HIT_PX_PHONE, right - left - 8)) : size;
  return { type: "group", children: [
    { type: "rect", shape: { x: left + 4, y: top - 1, width: hit, height: h + 2 }, style: { fill: "transparent" } },
    { type: "text", style: { x: left + 4 + size / 2, y: top + h / 2, text: bar.expanded ? GLYPH.expanded : GLYPH.collapsed,
      fill: c.thema[LOOK[bar.state].ink], fontSize: 12, align: "center", verticalAlign: "middle" } },
  ] };
}

// ── Marken (D24) ───────────────────────────────────────────────────────────────────────────────────────────────

/** Meilenstein als Raute, Fremdeintrag als Punkt (D24); Kantenlänge bzw. Durchmesser in Pixeln. */
export type MarkerShape = "DIAMOND" | "DOT";
const MARKER_PX: Readonly<Record<MarkerShape, number>> = { DIAMOND: 14, DOT: 10 };
/** Weniger Platz als dies bis zum Tagesende (Telefon: rund 34 px) lässt nur „R…“ übrig — dann steht keine Beschriftung,
 *  die Marke sagt alles im Sofort-Popup. */
export const MARKER_LABEL_MIN_PX = 40;
/** Eine Marke der Fläche mit Datenpunkt `[dayMs(Tag), dayMs(Tag) + 1 Tag, laneTopPx, laneBottomPx]` — der Tag selbst;
 *  den Überzug bis „jetzt“ liest die Zeichnung aus `marker.overdueFrom`. */
export type MarkerItem = { marker: TimelineMarker; rowId: string; value: [number, number, number, number]; shape: MarkerShape;
  selected: boolean };

/** Die Datenpunkte der Serie `markers` eines Blocks: Zeilenfolge, je Zeile die sichtbaren Marken in Rangfolge
 *  (`row.markers`, = Tastaturfolge); die Auswahl zählt in `row.markers` wie bei Balken in `row.bars`. */
export function markerItems(o: TimelineBodyOptions): MarkerItem[] {
  return o.group.rows.slice(o.block.from, o.block.to).flatMap((row, i) => {
    const top = o.layout.rows[o.block.from + i]!.top - o.block.top;
    return row.markers.map((marker, k): MarkerItem => ({ marker, rowId: row.id,
      value: [dayMs(marker.from), dayMs(marker.from) + DAY_MS, top + marker.lane * LANE_PX, top + (marker.lane + 1) * LANE_PX],
      shape: marker.item.source === "MILESTONE" ? "DIAMOND" : "DOT",
      selected: o.selection !== null && o.selection.rowId === row.id && o.selection.barIndex === k }));
  });
}

/** D24: Überzug 2 px gestreift vom Ende des Tages bis „jetzt“ (wie am Balken, S9), darüber Raute bzw. Punkt mittig in
 *  der Zustandsfarbe (erledigt blass, S10), in der Stufe Tag Nummer bzw. Titel rechts daneben bis zum Ende des Tages,
 *  Rahmen der Auswahl. */
function markerShapes(api: RenderApi, { marker, shape, selected }: MarkerItem, c: ShapeContext, labelled: boolean): Shape {
  const t = c.thema;
  const at = (ms: number): number => api.coord([ms, 0])[0]!;
  const [x0 = 0, y0 = 0] = api.coord([api.value(0), api.value(2)]);
  const [x1 = 0, y1 = 0] = api.coord([api.value(1), api.value(3)]);
  const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2 + ROW_PAD_PX / 2, r = MARKER_PX[shape] / 2;
  const children: Shape[] = [];
  if (marker.overdueFrom !== null) {
    const xDue = at(dayMs(marker.overdueFrom) + DAY_MS), xNow = at(c.nowMs);
    if (xNow > xDue) {
      children.push({ type: "rect", shape: { x: xDue, y: cy - 1, width: xNow - xDue, height: 2 },
        style: { fill: streifenMuster(t.fehler, t.flaeche) } });
    }
  }
  const style: Shape = { fill: t[LOOK[marker.state].area], ...(marker.state === "DONE" ? { opacity: DONE_OPACITY } : {}) };
  children.push(shape === "DIAMOND"
    ? { type: "polygon", shape: { points: [[cx, cy - r], [cx + r, cy], [cx, cy + r], [cx - r, cy]] }, style }
    : { type: "circle", shape: { cx, cy, r }, style });
  const x = cx + r + 4;
  if (labelled && x1 - x >= MARKER_LABEL_MIN_PX) {
    children.push({ type: "text", style: { x, y: cy, text: marker.item.reference ?? marker.item.title, fill: t.text, fontSize: 11,
      verticalAlign: "middle", overflow: "truncate", ellipsis: "…", width: Math.max(x1 - x, 0) } });
  }
  if (selected) {
    children.push({ type: "rect", shape: { x: cx - r - 2, y: cy - r - 2, width: 2 * r + 4, height: 2 * r + 4, r: 3 },
      style: { fill: "none", stroke: t.text, lineWidth: 2 } });
  }
  return { type: "group", children };
}

const ENCODE = { x: [0, 1], y: [2, 3] };

/** Gruppenfläche eines Blocks (P1–P5): y in Pixeln des Blocks, x unsichtbar mit dem Fenster, Serien `bars` (über der
 *  „jetzt“-Linie ohne Text), `toggles` (trägt das Raster unter den Balken) und `markers` (D24), Popup als DOM-Knoten. */
export function bodyOption(o: TimelineBodyOptions, items: readonly BarItem[] = blockItems(o),
    marks: readonly MarkerItem[] = markerItems(o)): Record<string, unknown> {
  const t = o.thema;
  const b = basis(o, false);
  const c: ShapeContext = { thema: t, phone: o.phone, nowMs: o.nowMs, companyColors: o.companyColors,
    hatch: { flaeche: hatchOf(t.flaeche), text: hatchOf(t.text) } };
  const toggles = items.filter((it) => it.bar.canExpand);
  const point = (it: BarItem) => ({ id: `${it.rowId}|${it.bar.id}`, name: it.bar.id, value: it.value });
  const tooltip = (it: BarItem): string => TIMELINE_TEXT.tooltip(it.bar, o.heute,
    it.bar.duplicateOf === null ? undefined : o.teamNames.get(it.bar.duplicateOf));
  const day = b.stage === "DAY";
  return {
    grid: { left: 0, right: 0, top: 0, bottom: 0, containLabel: false, outerBoundsMode: "none" },
    xAxis: { ...b.xAxis, show: false },
    yAxis: { type: "value", min: 0, max: o.block.height, inverse: true, show: false },
    dataZoom: b.dataZoom,
    // Versteckt und ohne Symbole: nur Träger von „Zeitraum aufziehen“ (takeGlobalCursor, S14 Zoom).
    toolbox: { show: true, itemSize: 0, showTitle: false, left: 0, top: 0,
      feature: { dataZoom: { yAxisIndex: "none", brushStyle: { color: t.raster, opacity: 0.4, borderColor: t.text, borderWidth: 1 } } } },
    tooltip: { ...popup(), triggerOn: o.phone ? "none" : "mousemove|click",
      formatter: (p: { seriesName?: string; dataIndex: number }) => {
        const it = items[p.dataIndex], m = marks[p.dataIndex];
        return popupNode(p.seriesName === "bars" && it !== undefined ? tooltip(it)
          : p.seriesName === "markers" && m !== undefined ? TIMELINE_TEXT.dueTooltip(m.marker.item, o.heute) : "");
      } },
    series: [
      { type: "custom", name: "bars", clip: true, encode: ENCODE, data: items.map(point),
        renderItem: (p: RenderParams, api: RenderApi) => barShapes(api, items[p.dataIndex]!, p.coordSys, c),
        markArea: b.shade, markLine: { ...LINES, data: [b.now] } },
      { type: "custom", name: "toggles", clip: true, encode: ENCODE, tooltip: { show: false }, data: toggles.map(point),
        renderItem: (p: RenderParams, api: RenderApi) => toggleShapes(api, toggles[p.dataIndex]!.bar, p.coordSys, c),
        markLine: { ...LINES, z: 1, data: b.raster } },
      { type: "custom", name: "markers", clip: true, encode: ENCODE,
        data: marks.map((m) => ({ id: `${m.rowId}|${m.marker.id}`, name: m.marker.id, value: m.value })),
        renderItem: (p: RenderParams, api: RenderApi) => markerShapes(api, marks[p.dataIndex]!, c, day) },
    ],
  };
}

// ── Komponente ─────────────────────────────────────────────────────────────────────────────────────────────────

/** Ein `div` (`.tl__achse` AXIS_PX hoch bzw. `.tl__flaeche` so hoch wie der Block) mit einer Zeichenfläche. Gesten:
 *  `datazoom` → `onWindow` (auf Tage gerundet, einmal bis zur nächsten Option, P3); Klick unterscheidet `toggles`,
 *  `bars` und `markers`; die Auswahl der Zeilenspalte zeigt das Popup am Balken bzw. an der Marke (S15, D24); am
 *  Telefon Popup nur per langem Drücken (P4). */
export function TicketTimelineChart(p: TicketTimelineChartProps): ReactElement {
  const body = p.kind === "body" ? p : null;
  // Die Auswahl zählt nur in dem Block, der ihre Zeile zeigt — Pfeiltasten bauen nicht jede Fläche neu (G4).
  const sel = body?.selection ?? null;
  const selKey = sel !== null && body!.group.rows.slice(body!.block.from, body!.block.to).some((r) => r.id === sel.rowId)
    ? `${sel.rowId}|${sel.barIndex}` : null;
  const items = useMemo(() => (body === null ? [] : blockItems(body)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- die Felder, aus denen blockItems liest
    [body?.group, body?.layout, body?.block, selKey]);
  const marks = useMemo(() => (body === null ? [] : markerItems(body)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- die Felder, aus denen markerItems liest
    [body?.group, body?.layout, body?.block, selKey]);
  const option = useMemo(() => (body === null ? axisOption(p) : bodyOption(body, items, marks)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- die Felder der Optionen, nicht die Rückrufe
    [p.kind, p.window, p.stage, p.heute, p.nowMs, p.kalender, p.thema, p.phone, items, marks, body?.companyColors, body?.teamNames]);
  // Popup und Auswahl gehören der Serie der Gruppe: Balken (0) in Teamgruppen, Marken (2) in Termingruppen (D24).
  const tipSeries = body === null || body.group.kind === "TEAM" ? 0 : 2;
  const tipSeriesRef = useRef(tipSeries);
  tipSeriesRef.current = tipSeries;

  const locked = useRef(false);          // P3: ein Fenster je Option melden, kein Schaukeln
  const drift = useRef(false);           // M1: ECharts steht neben dem Fenster (Rest einer Geste unter der Rundung)
  const pressed = useRef(false);         // ein Zeiger ist gedrückt (Geste läuft)
  const windowRef = useRef(p.window);
  windowRef.current = p.window;
  const hovered = useRef<number | null>(null);
  const suppressClick = useRef(false);   // langes Drücken öffnet nicht (Spec „Telefon“)
  const press = useRef<{ x: number; y: number; timer: ReturnType<typeof setTimeout> } | null>(null);

  const d = useDiagramm(option, {
    datazoom: ({ startValue, endValue }) => {
      const w = windowFromMs(startValue, endValue);
      drift.current = startValue !== dayMs(p.window.from) || endValue !== dayMs(p.window.to) + DAY_MS;
      if (locked.current || (w.from === p.window.from && w.to === p.window.to)) { snap(); return; }
      locked.current = true;
      p.onWindow(w);
    },
    click: (e) => {
      // Eine Marke über ihren Datenpunkt (ECharts nennt dazu `name`, die Attrappe nicht immer).
      const key = e.seriesName === "markers" ? marks[e.dataIndex ?? -1]?.marker.id : e.name;
      if (body === null || key === undefined) return;
      if (suppressClick.current) { suppressClick.current = false; return; }
      closeTip();
      if (e.seriesName === "toggles") body.onToggle(key);
      else if (e.seriesName === "bars") body.onBarClick(key);
      else if (e.seriesName === "markers") body.onMarkerClick(key);
    },
    mouseover: (e) => {
      if (body === null || (e.seriesName !== "bars" && e.seriesName !== "markers")) return;
      hovered.current = e.dataIndex ?? null;
      if (e.seriesName === "bars") body.onHover?.(e.name ?? null);
    },
    mouseout: () => { hovered.current = null; body?.onHover?.(null); },
  });
  const instanzRef = useRef(d.instanz);
  instanzRef.current = d.instanz;
  useEffect(() => { locked.current = false; drift.current = false; }, [option]);

  // M1: Ein ungemeldeter Rest einer Geste (unter der Rundung auf Tage) rastet am Gestenende auf das Fenster ein — auch
  // losgelassen außerhalb der Fläche, auch nach Pinch, auch wenn das gedrosselte letzte datazoom erst danach kommt.
  // Sonst stünde diese Fläche neben Kopfachse und Nachbargruppen (S14a). Das folgende datazoom ist „unverändert“.
  const snap = useRef(function snapToWindow(): void {
    if (!drift.current || locked.current || pressed.current) return;
    drift.current = false;
    const w = windowRef.current;
    instanzRef.current?.dispatchAction({ type: "dataZoom", dataZoomIndex: 0, startValue: dayMs(w.from), endValue: dayMs(w.to) + DAY_MS });
  }).current;
  useEffect(() => {
    const down = (): void => { pressed.current = true; };
    const up = (): void => { pressed.current = false; snap(); };
    window.addEventListener("pointerdown", down, true);
    window.addEventListener("pointerup", up, true);
    window.addEventListener("pointercancel", up, true);
    return () => {
      window.removeEventListener("pointerdown", down, true);
      window.removeEventListener("pointerup", up, true);
      window.removeEventListener("pointercancel", up, true);
    };
  }, [snap]);

  // M3: Ein Popup, das diese Fläche selbst geöffnet hat (langes Drücken, Auswahl), schließt beim nächsten Drücken, Klick
  // und Rollen — mit `triggerOn: "none"` blendet ECharts es nie von selbst aus. Beide Helfer sind stabil (nur Refs).
  const tipOpen = useRef(false);
  /** Nacharbeit Popup: das Auswahl-Popup, das auf das Bild seiner Option wartet (siehe unten). */
  const waiting = useRef<number | null>(null);
  const closeTip = useRef(function close(): void {
    waiting.current = null;
    if (!tipOpen.current) return;
    tipOpen.current = false;
    tipShown.current = false;
    instanzRef.current?.dispatchAction({ type: "hideTip" });
    document.removeEventListener("scroll", close, true);
  }).current;
  const openTip = useRef((dataIndex: number): void => {
    instanzRef.current?.dispatchAction({ type: "showTip", seriesIndex: tipSeriesRef.current, dataIndex });
    if (!tipOpen.current) document.addEventListener("scroll", closeTip, { capture: true, passive: true });
    tipOpen.current = true;
  }).current;
  useEffect(() => () => { document.removeEventListener("scroll", closeTip, true); }, [closeTip]);

  // Nacharbeit Popup (Aufgabe 8): `useDiagramm` setzt die Option mit `lazyUpdate` — ECharts zeichnet sie erst im nächsten
  // Bild. Ein `showTip` davor trifft die alte Zeichnung (die erste Wahl zeigte kein Popup), und beim Zeichnen zeigt der
  // Tooltip das zuletzt gezeigte Popup an dessen gemerktem Ort wieder (`_keepShow`, nach dem eigenen showTip: der
  // vorige Inhalt stand am neuen Ort). Jede neue Auswahl ist eine neue Option (Auswahlrahmen): das alte Popup schließt
  // sofort (`hideTip` lässt ECharts den Ort vergessen), das neue wartet auf `finished` (gezeichnet, nichts mehr
  // ausstehend). Steht die Option schon, öffnet es sofort.
  const drawn = useRef<unknown>(null);
  const optionRef = useRef(option);
  optionRef.current = option;
  useEffect(() => {
    const instanz = d.instanz;
    if (instanz === null) return undefined;
    const done = (): void => {
      drawn.current = optionRef.current;
      const k = waiting.current;
      waiting.current = null;
      if (k !== null) openTip(k);
    };
    instanz.on("finished", done);
    return () => { instanz.off("finished", done); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openTip liest nur Refs und die Instanz
  }, [d.instanz]);

  // K1: ohne Popup (Dialog offen) bleibt der Rahmen in der Option, nur das Popup schweigt.
  const selectedIndex = body?.selectionTip === false ? -1 : (tipSeries === 0 ? items : marks).findIndex((it) => it.selected);
  const tipShown = useRef(false);
  useEffect(() => {
    if (d.instanz === null) return;
    if (selectedIndex >= 0) {
      if (drawn.current === option) openTip(selectedIndex);
      else {
        if (tipOpen.current) closeTip(); else instanzRef.current?.dispatchAction({ type: "hideTip" });
        waiting.current = selectedIndex;
      }
      tipShown.current = true;
    } else if (tipShown.current) {
      closeTip();
    } else {
      waiting.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openTip/closeTip lesen nur Refs und die Instanz; die Option
    // gehört zur selben Auswahl
  }, [d.instanz, selectedIndex]);

  const frameMode = body?.frameMode === true;
  useEffect(() => {
    if (d.instanz === null || p.kind !== "body") return;
    d.instanz.dispatchAction({ type: "takeGlobalCursor", key: "dataZoomSelect", dataZoomSelectActive: frameMode });
  }, [d.instanz, p.kind, frameMode]);

  const stopPress = (): void => {
    if (press.current !== null) clearTimeout(press.current.timer);
    press.current = null;
  };
  useEffect(() => stopPress, []);
  const longPress = p.phone && body !== null ? {
    onPointerDown: (e: ReactPointerEvent<HTMLDivElement>) => {
      stopPress();
      closeTip();
      suppressClick.current = false;
      press.current = { x: e.clientX, y: e.clientY, timer: setTimeout(() => {
        press.current = null;
        if (hovered.current === null) return;
        openTip(hovered.current);
        suppressClick.current = true;
      }, LONG_PRESS_MS) };
    },
    onPointerMove: (e: ReactPointerEvent<HTMLDivElement>) => {
      const s = press.current;
      if (s !== null && Math.hypot(e.clientX - s.x, e.clientY - s.y) > LONG_PRESS_SLOP_PX) stopPress();
    },
    onPointerUp: stopPress,
    onPointerCancel: stopPress,
  } : {};

  // Die Zeichenfläche ist Bild: Namen, Zustände und Wege trägt die Zeilenspalte (Tastatur, Vorlesestimme, S15).
  return (
    <div ref={d.ref} className={body === null ? "tl__achse" : "tl__flaeche"} aria-hidden="true"
      style={{ height: body === null ? AXIS_PX : body.block.height }} {...longPress} />
  );
}
